import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Last-resort catch for render errors anywhere in the tree. Without this,
 * a component that throws during render unmounts the whole app and the
 * user is left staring at a blank white page (the pre-fix symptom of the
 * LoginPage hooks-order bug). Shows a reload prompt instead.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  override state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Fire-and-forget report so crashes show up in Slack like auth issues do.
    const apiBase = import.meta.env.VITE_API_URL ?? "/api";
    fetch(`${apiBase}/auth-debug`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        step: "render-crash",
        error: `${error.message}\n${info.componentStack ?? ""}`.slice(0, 2000),
      }),
    }).catch(() => {});
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          background: "var(--c-bg, #f4f1ea)",
          color: "var(--c-text, #2b2b28)",
          fontFamily: "var(--font-body, system-ui, sans-serif)",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 420 }}>
          <h1 style={{ fontFamily: "var(--font-heading, serif)", marginBottom: "0.5rem" }}>
            Something went wrong
          </h1>
          <p style={{ marginBottom: "1.5rem", opacity: 0.75 }}>
            An unexpected error stopped the page from loading. Reloading
            usually fixes it — nothing you did caused this.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: "0.6rem 1.5rem",
              borderRadius: "999px",
              border: "none",
              cursor: "pointer",
              background: "var(--c-primary, #213d28)",
              color: "#fff",
              fontSize: "1rem",
            }}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
